import type ApolloClient from 'apollo-client';
import gql from 'graphql-tag';
import { fg } from '@atlaskit/platform-feature-flags';
import { fetchQuery, graphql } from 'react-relay';
import type { queryTypedChildrenContentQuery } from './__generated__/queryTypedChildrenContentQuery.graphql.ts';
import { type Environment } from 'relay-runtime';

const GET_CUSTOM_CONTENT = gql`
	query CustomContent($id: ID!, $type: String!) {
		content(id: $id) {
			nodes {
				ari
				children(first: 100, type: $type) {
					nodes {
						body {
							atlas_doc_format {
								value
							}
						}
						title
					}
				}
			}
		}
	}
`;

export type CustomContentResponse = {
	data: {
		content: {
			nodes: {
				ari: string;
				children: {
					nodes: {
						body: {
							atlas_doc_format: {
								value: string;
							};
						};
						title: string;
					}[];
					pageInfo: {
						hasNextPage: boolean;
					};
				};
			}[];
		};
	};
};

const formatResponseNodes = (
	content: CustomContentResponse['data']['content']['nodes'][0]['children']['nodes'],
): Record<string, any> | null => {
	return content.reduce((acc, node) => {
		return {
			...acc,
			[node.title]: JSON.parse(node.body.atlas_doc_format.value),
		};
	}, {});
};

// Content type -> container ID -> keyed children (value)
const containerChildrenContentCache: Record<
	string,
	Record<
		string,
		{
			promise: Promise<Record<string, any> | null> | null;
			values: Record<string, any> | null;
		}
	>
> = {};

export const getCachedTypedChildrenContent = ({
	contentContainerId,
	childContentType,
}: {
	childContentType: string;
	contentContainerId: string;
}) => {
	return containerChildrenContentCache[childContentType]?.[contentContainerId]?.values;
};

export const queryTypedChildrenContent = async ({
	apolloClient,
	contentContainerId,
	childContentType,
	relayEnvironment,
}: {
	apolloClient: ApolloClient<object>;
	childContentType: string;
	contentContainerId: string;
	relayEnvironment?: Environment;
}) => {
	const containerCache = (containerChildrenContentCache[childContentType] ??= {});
	const contentCache = (containerCache[contentContainerId] ??= { promise: null, values: null });

	if (relayEnvironment && fg('forge-ui-add-relay-get-custom-content-query')) {
		const children = (contentCache.promise ??= new Promise<any>((resolve, reject) => {
			let allNodes: any[] = [];

			// Page size needs to be < 100 for manual pagination to work correctly..
			// >=100 results in pageInfo.hasNextPage always returning false.
			const pageSize = 99;
			let pageOffset = 0;
			const MAX_TOTAL_ITEMS = 1000;
			let hasNextPage = true;

			const fetchAllPages = async () => {
				try {
					while (hasNextPage && allNodes.length < MAX_TOTAL_ITEMS) {
						const data = await new Promise<any>((pageResolve, pageReject) => {
							fetchQuery<queryTypedChildrenContentQuery>(
								relayEnvironment,
								graphql`
									query queryTypedChildrenContentQuery(
										$id: ID!
										$type: String!
										$pageSize: Int!
										$pageOffset: Int!
									) {
										content(id: $id) {
											nodes {
												children(first: $pageSize, offset: $pageOffset, type: $type) {
													pageInfo {
														hasNextPage
													}
													nodes {
														body {
															atlas_doc_format {
																value
															}
														}
														title
													}
												}
											}
										}
									}
								`,
								{
									id: contentContainerId,
									type: childContentType,
									pageSize,
									pageOffset,
								},
								{
									fetchPolicy: 'network-only',
								},
							).subscribe({
								next: pageResolve,
								error: pageReject,
							});
						});

						const { nodes = [], pageInfo } = data?.content?.nodes?.[0]?.children || {};
						const nextPage = pageInfo?.hasNextPage ?? false;
						allNodes.push(...nodes);

						// Stop loading if total count exceeds 1000
						if (allNodes.length >= MAX_TOTAL_ITEMS) {
							resolve(allNodes);
							return;
						}

						hasNextPage = nextPage;
						pageOffset += pageSize;
					}

					resolve(allNodes);
				} catch (error) {
					reject(error);
				}
			};

			fetchAllPages();
		})
			.then((responseNodes) => {
				if (!responseNodes) {
					return null;
				}
				const content = formatResponseNodes(responseNodes as any);
				contentCache.values = content;
				return content;
			})
			.catch(() => {
				return null;
			}));

		return children;
	}

	const children = (contentCache.promise ??= apolloClient
		.query({
			query: GET_CUSTOM_CONTENT,
			variables: {
				id: contentContainerId,
				type: childContentType,
			},
			// Manual cache management in place.
			fetchPolicy: 'network-only',
		})
		.then(({ data }) => data.content?.nodes?.[0]?.children?.nodes || [])
		.then((responseNodes) => {
			if (!responseNodes) {
				return null;
			}
			const content = formatResponseNodes(responseNodes);
			contentCache.values = content;
			return content;
		})
		.catch(() => null));

	return children;
};
